/* Lo Esencial — Favoritos panel inside the cart drawer
 *
 * Companion to assets/wishlist.js (the store) and assets/cart-drawer.js (the
 * drawer). This file owns only the "Favoritos" tab and its panel.
 *
 * WHY IT IS A SEPARATE ASSET AND NOT A <script> IN THE SECTION
 * assets/cart-drawer.js replaceDrawer() re-renders the drawer by parsing the
 * Section Rendering API response with DOMParser and swapping
 * .cart-drawer__inner. Scripts inserted that way NEVER execute, and any
 * listener bound to a node inside .cart-drawer__inner dies with it. So:
 *   - every listener here is delegated at document level, and
 *   - the panel is re-rendered on le:cart:updated (drawer HTML replaced) as
 *     well as on le:wishlist:updated (list changed).
 *
 * The list itself is client-side only, so Liquid cannot pre-render it; the
 * panel ships an empty <ul> plus a <template> and gets filled in here.
 */
(function () {
  'use strict';

  var INNER = '.cart-drawer__inner';
  var PANEL = '[data-cart-wishlist]';

  // Which view the drawer is showing. Module-scoped on purpose: it must
  // outlive the DOM nodes, which replaceDrawer() throws away wholesale.
  var activeView = 'cart';

  // ---- shared add-to-cart form -------------------------------------------
  // cart-drawer.js delegates submits on [data-cart-rec] at document level and
  // owns the whole add path from there (AJAX add, section re-render, cart
  // count, drawer open). Reusing it means zero changes to that file.
  var addForm = null;
  var addIdInput = null;

  function ensureAddForm() {
    if (addForm && document.body.contains(addForm)) return addForm;
    addForm = document.createElement('form');
    addForm.action = '/cart/add';
    addForm.method = 'post';
    addForm.enctype = 'multipart/form-data';
    addForm.setAttribute('data-cart-rec', '');
    addForm.setAttribute('data-cd-wl-form', '');
    addForm.hidden = true;

    addIdInput = document.createElement('input');
    addIdInput.type = 'hidden';
    addIdInput.name = 'id';

    var qty = document.createElement('input');
    qty.type = 'hidden';
    qty.name = 'quantity';
    qty.value = '1';

    addForm.appendChild(addIdInput);
    addForm.appendChild(qty);
    document.body.appendChild(addForm);
    return addForm;
  }

  // ---- helpers -----------------------------------------------------------
  function money(cents) {
    // Mirrors the formatter in sections/le-wishlist.liquid so the drawer and
    // the dedicated page never disagree on how a price reads.
    var n = (cents || 0) / 100;
    var s = n % 1 === 0
      ? n.toLocaleString('en-US')
      : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + s;
  }

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function list() {
    return (window.LEWishlist && window.LEWishlist.all()) || [];
  }

  // The stored item's url/img were written from theme data-* attributes, but
  // they round-trip through localStorage, which any script on the origin can
  // write. Never hand an unvetted string to href/src: a `javascript:` url in
  // an href is a click away from executing. Allow only same-origin absolute
  // urls and root-relative paths; anything else degrades to no link / no image
  // rather than rendering something we cannot vouch for.
  function safeUrl(raw) {
    if (!raw || typeof raw !== 'string') return '';
    try {
      var u = new URL(raw, window.location.origin);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.href;
    } catch (e) {
      return '';
    }
  }

  // ---- view switching ----------------------------------------------------
  // The drawer header is Liquid-rendered for the CART ("Tu carrito / 0
  // objetos"), which reads as nonsense sitting on top of a list of saved
  // items. Swap it with the view and put the cart copy back on the way out.
  // The server-rendered text is stashed on first sight of each fresh node:
  // replaceDrawer() always lands us back in the cart view, so what is
  // captured is always the real cart copy, never a wishlist string.
  function applyHeader(inner) {
    var eyebrow = inner.querySelector('.cart-drawer__eyebrow');
    var title = inner.querySelector('.cart-drawer__title');
    if (!eyebrow || !title) return;

    if (!eyebrow.dataset.cartText) eyebrow.dataset.cartText = eyebrow.textContent.trim();
    if (!title.dataset.cartText) title.dataset.cartText = title.textContent.trim();

    if (activeView === 'wishlist') {
      var n = list().length;
      eyebrow.textContent = 'Tus favoritos';
      title.textContent = n === 1 ? '1 objeto guardado' : n + ' objetos guardados';
    } else {
      eyebrow.textContent = eyebrow.dataset.cartText;
      title.textContent = title.dataset.cartText;
    }
  }

  // The Favoritos badge is [data-wishlist-count], which wishlist.js owns -- but
  // it only refreshes those on le:wishlist:updated. A cart change re-renders
  // the drawer from Liquid, and Liquid cannot see a localStorage list, so the
  // badge comes back server-rendered as a hidden "0" and STAYS wrong until the
  // saved list happens to change. Add an item to the cart with two favourites
  // saved and the tab reads 0. Re-sync it on every re-render.
  function syncCountBadges(inner) {
    var n = list().length;
    inner.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  function applyView() {
    var inner = document.querySelector(INNER);
    if (!inner) return;
    inner.classList.toggle('is-wishlist-view', activeView === 'wishlist');
    inner.querySelectorAll('[data-cart-view]').forEach(function (btn) {
      var on = btn.dataset.cartView === activeView;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    applyHeader(inner);
    syncCountBadges(inner);
  }

  function setView(view) {
    activeView = view === 'wishlist' ? 'wishlist' : 'cart';
    applyView();
    if (activeView === 'wishlist') render();
  }

  // ---- render ------------------------------------------------------------
  function render() {
    var panel = document.querySelector(PANEL);
    if (!panel) return;

    var ul = panel.querySelector('[data-cd-wl-list]');
    var empty = panel.querySelector('[data-cd-wl-empty]');
    var all = panel.querySelector('[data-cd-wl-all]');
    var tpl = panel.querySelector('[data-cd-wl-template]');
    if (!ul || !tpl) return;

    var items = list();
    ul.innerHTML = '';

    if (!items.length) {
      ul.hidden = true;
      if (empty) empty.hidden = false;
      if (all) all.hidden = true;
      return;
    }

    ul.hidden = false;
    if (empty) empty.hidden = true;
    if (all) all.hidden = false;

    items.forEach(function (it) {
      var node = tpl.content.firstElementChild.cloneNode(true);
      node.dataset.cdWlId = it.id;

      var href = safeUrl(it.url);
      node.querySelectorAll('[data-cd-wl-link]').forEach(function (a) {
        if (href) a.href = href;
        else a.removeAttribute('href');
      });

      var img = node.querySelector('[data-cd-wl-img]');
      var imgSrc = safeUrl(it.img);
      if (imgSrc) {
        img.src = imgSrc;
        img.alt = it.t || '';
      } else {
        var media = img.closest('.cart-drawer__wl-media') || img;
        media.remove();
        // Dropping the node does not reclaim its grid column, which would
        // leave a 64px hole where the thumb was. Collapse the row instead.
        node.classList.add('is-noimg');
      }

      node.querySelector('[data-cd-wl-title]').textContent = it.t || '';

      var vEl = node.querySelector('[data-cd-wl-variant]');
      if (it.v && it.v !== 'Default Title') vEl.textContent = it.v;
      else vEl.remove();

      node.querySelector('[data-cd-wl-price]').textContent = money(it.p);
      ul.appendChild(node);
    });
  }

  // ---- delegated interaction --------------------------------------------
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;

    var tab = t.closest('[data-cart-view]');
    if (tab && tab.closest('.cart-drawer__tabs')) {
      ev.preventDefault();
      setView(tab.dataset.cartView);
      return;
    }

    var card = t.closest('[data-cd-wl-card]');
    if (!card) return;

    if (t.closest('[data-cd-wl-remove]')) {
      // Let the row fade before it leaves the list; the store write is what
      // triggers the re-render, so it has to wait for the transition.
      card.classList.add('is-removing');
      var wait = reduceMotion() ? 0 : 220;
      setTimeout(function () {
        if (window.LEWishlist) window.LEWishlist.remove(card.dataset.cdWlId);
      }, wait);
      return;
    }

    var addBtn = t.closest('[data-cd-wl-add]');
    if (!addBtn || addBtn.disabled) return;

    addBtn.disabled = true;
    var label = addBtn.textContent;
    addBtn.textContent = 'Agregando...';

    ensureAddForm();
    addIdInput.value = card.dataset.cdWlId;
    if (typeof addForm.requestSubmit === 'function') {
      addForm.requestSubmit();
    } else {
      addForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    // The button lives inside .cart-drawer__inner, which replaceDrawer()
    // destroys on success -- so this restore only matters on the failure
    // path, where the node survives and must not stay stuck on "Agregando...".
    var release = function () {
      addBtn.disabled = false;
      addBtn.textContent = label;
      document.removeEventListener('le:cart:updated', release);
    };
    document.addEventListener('le:cart:updated', release, { once: true });
    setTimeout(release, 8000);
  });

  // ---- re-hydration ------------------------------------------------------
  // The drawer's HTML was just swapped: the view class and the tab state are
  // gone with it. Snap back to the cart view -- a cart change is always the
  // result of an add, and the customer needs to SEE the item land, which is
  // the whole point of the fly-to-cart landing on the Carrito tab.
  document.addEventListener('le:cart:updated', function () {
    activeView = 'cart';
    applyView();
    render();
  });

  // The saved list changed (heart toggled anywhere, or another tab).
  // applyView() and not just render(): while the Favoritos view is open the
  // drawer title carries the saved count, so it has to move with the list.
  document.addEventListener('le:wishlist:updated', function () {
    render();
    applyView();
  });

  function init() {
    applyView();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
